import React from "react";

import Centile from "pages/customer/tools/location/filters/targets/Centile";
import CentileMetric from "pages/customer/tools/location/filters/targets/CentileMetric";

import {
    selectCandidateComparatorStores,
    selectCandidateTarget,
    selectAggregatedDirectCompetitors,
    selectRetailCentresCentiles,
    selectStoreCategoryCentilesMetrics,
    setCandidateTarget
} from "modules/customer/tools/location/filters/filtersSlice";
import { useAppDispatch, useAppSelector } from "store";
import numberFormatter from "utils/numberFormatter";

const Diversity: React.FC = () => {
    const dispatch = useAppDispatch();
    const candidateComparatorStores = useAppSelector(selectCandidateComparatorStores);
    const retailCentresCentiles = useAppSelector(selectRetailCentresCentiles);
    const comparatorStoresCentiles = candidateComparatorStores.map(store => {
        const retailCentreCentile = retailCentresCentiles.find(rcc => rcc.retailCentreId === store.retailCentre.id)?.diversity ?? 0;
        return {
            storeName: store.name,
            centileValue: retailCentreCentile
        };
    });
    const aggregatedDirectCompetitors = useAppSelector(selectAggregatedDirectCompetitors);
    const aggregatedDirectCompetitorCentiles = aggregatedDirectCompetitors.map(item => ({
        storeName: item.fascia,
        centileValue: item.diversityCentile
    }));
    const candidateTarget = useAppSelector(selectCandidateTarget);
    const storeCategoryCentilesMetrics = useAppSelector(selectStoreCategoryCentilesMetrics);
    const centileMetric = storeCategoryCentilesMetrics.find(cm => cm.centile === candidateTarget?.diversity);
    const name = "Diversity";
    const description = `The diversity score looks at the catchment area population and evaluates the resident's
        ethnicity, first language, and birth country.`;
    const diversityCentileMetrics = [{
        name: "Minority ethnic",
        tooltip: "The percentage of the catchment area's population that is non-White British.",
        value: numberFormatter.toPercentage(centileMetric?.minorityEthnicPercentage ?? 0, false, 0)
    }, {
        name: "Non-English speakers",
        tooltip: "The percentage of the catchment area's population that don't speak English or Welsh as their first language.",
        value: numberFormatter.toPercentage(centileMetric?.languageNotEnglishOrWelshPercentage ?? 0, false, 0)
    }, {
        name: "Born outside of UK",
        tooltip: "The percentage of the catchment area's population that are migrants.",
        value: numberFormatter.toPercentage(centileMetric?.notBornInUkPercentage ?? 0, false, 0)
    }];

    const handleSliderChange = (value: number) => {
        if (candidateTarget) {
            const newCandidateTarget = {
                ...candidateTarget,
                diversity: value
            };
            dispatch(setCandidateTarget(newCandidateTarget));
        }
    };

    if (!candidateTarget) {
        return null;
    }

    return (
        <Centile
            name={name}
            description={description}
            disabled={!candidateTarget.useDemographics}
            sliderValue={candidateTarget.diversity}
            onSliderChange={handleSliderChange}
            comparatorStoresCentiles={comparatorStoresCentiles}
            aggregatedDirectCompetitorCentiles={aggregatedDirectCompetitorCentiles}
            chartLowLabel="Less diverse population"
            chartHighLabel="Very diverse population"
        >
            {diversityCentileMetrics.map((metric, index) =>
                <CentileMetric
                    key={index}
                    name={metric.name}
                    tooltip={metric.tooltip}
                    value={metric.value}
                    disabled={!candidateTarget.useDemographics}
                />
            )}
        </Centile>
    );
};

export default Diversity;
